// Debug logging utility - uses centralized logger
// Used for debugging subscription loading and other issues
// CRITICAL: Always defers execution to avoid React error #300 (updating during render)
import { logger } from './logger';

const REDACTED_VALUE = '[REDACTED]';
const SENSITIVE_KEYWORDS = [
  'email',
  'phone',
  'password',
  'passcode',
  'token',
  'authorization',
  'cookie',
  'session',
  'secret',
  'apikey',
  'api_key',
  'access_token',
  'id_token',
  'refresh_token',
  'resume',
  'coverletter',
  'cover_letter',
  'ssn',
  'socialsecurity',
  'address',
  'linkedin',
];

const EMAIL_REGEX = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PHONE_REGEX = /\b(?:\+?1[-.\s]?)?(?:\(\d{3}\)|\d{3})[-.\s]?\d{3}[-.\s]?\d{4}\b/g;
const BEARER_REGEX = /\bBearer\s+[A-Za-z0-9\-_.=]+\b/g;
const JWT_REGEX = /\b[A-Za-z0-9\-_]{10,}\.[A-Za-z0-9\-_]{10,}\.[A-Za-z0-9\-_]{10,}\b/g;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return Object.prototype.toString.call(value) === '[object Object]';
};

const isIdLikeKey = (key: string): boolean => {
  return (
    key.endsWith('id') ||
    key.endsWith('_id') ||
    key.endsWith('ids') ||
    key.endsWith('_ids')
  );
};

const isSensitiveKey = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  if (isIdLikeKey(lowerKey) && (lowerKey.includes('resume') || lowerKey.includes('coverletter'))) {
    return false;
  }
  return SENSITIVE_KEYWORDS.some((keyword) => lowerKey.includes(keyword));
};

const redactString = (value: string): string => {
  let redacted = value;
  redacted = redacted.replace(BEARER_REGEX, 'Bearer ' + REDACTED_VALUE);
  redacted = redacted.replace(JWT_REGEX, REDACTED_VALUE);
  redacted = redacted.replace(EMAIL_REGEX, REDACTED_VALUE);
  redacted = redacted.replace(PHONE_REGEX, REDACTED_VALUE);
  return redacted;
};

export const redactPII = (value: unknown, seen = new WeakMap<object, unknown>()): unknown => {
  if (value == null) {
    return value;
  }
  if (typeof value === 'string') {
    return redactString(value);
  }
  if (typeof value !== 'object') {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: redactString(value.message),
    };
  }
  if (seen.has(value)) {
    return seen.get(value);
  }
  if (Array.isArray(value)) {
    const redactedArray: unknown[] = [];
    seen.set(value, redactedArray);
    for (const item of value) {
      redactedArray.push(redactPII(item, seen));
    }
    return redactedArray;
  }
  if (!isPlainObject(value)) {
    return value;
  }
  const redactedObject: Record<string, unknown> = {};
  seen.set(value, redactedObject);
  for (const [key, item] of Object.entries(value)) {
    if (isSensitiveKey(key)) {
      redactedObject[key] = REDACTED_VALUE;
      continue;
    }
    redactedObject[key] = redactPII(item, seen);
  }
  return redactedObject;
};

export const redactDebugData = (logData: { message?: unknown; data?: unknown } = {}) => {
  const rawMessage = logData.message;
  const message = typeof rawMessage === 'string' ? redactString(rawMessage) : redactPII(rawMessage);
  const data = redactPII(logData.data || {});
  return { message, data };
};

export const sendDebugLog = (logData: any): void => {
  // Always defer to next tick to avoid React error #300
  setTimeout(() => {
    try {
      const redacted = redactDebugData(logData);
      logger.log('[DEBUG]', redacted.message, redacted.data || {});
    } catch (e) {
      // Silently fail if logger is not available
    }
  }, 0);
};
